// What to add to the tree next: the gaps that matter most, closest to the home person first.
// Each suggestion has a stable id, so one that's dismissed stays dismissed while the tree
// changes around it. Pure; tested in tests/suggestions.test.ts.

import { ageOf, ageText, countableDate, midAge, yearsBetween } from './countdown';
import { dateYear } from './dates';
import { childrenOf, graphOf, neighborsOf, parentsOf, partnersOf, siblingsOf, type Graph } from './graph';
import { birthDate, deathDate, displayName, isDeceased, isLiving, lifeStatus, nameToString, presumedDeceased, primaryName, pronounSet, shortName, termGender } from './people';
import { normalize, soundex } from './search';
import { dateProblems, type DateProblem } from './stats';
import type { Id, Person, Tree } from './types';

export type SuggestionKind =
  | 'home'
  | 'duplicate'
  | 'dates'
  | 'link-parents'
  | 'parents'
  | 'link-partners'
  | 'unlinked'
  | 'name'
  | 'birth-date'
  | 'gender'
  | 'living'
  | 'spelling'
  | 'partnership-date'
  | 'death-date'
  | 'sources'
  | 'photo'
  | 'birthplace'
  | 'cite-birth'
  | 'occupation';

/**
 * What each kind of suggestion is, how much it counts before closeness, and when it's made. The
 * Suggestions explainer prints this table, so the numbers here are the ones it describes.
 */
export const SUGGESTION_RULES: Record<SuggestionKind, { label: string; base: number; when: string }> = {
  home: { label: 'Home person', base: 100, when: 'Nobody is set as the home person. It offers whoever has the most links.' },
  duplicate: {
    label: 'Possible duplicates',
    base: 90,
    when: 'Two people with the same first and last name and the same birth year, birthplace, or relative in the same role (both children of one parent, say). Never people linked to each other, with different parents, or a child’s two parents.',
  },
  dates: {
    label: 'Dates to check',
    base: 85,
    when: 'Dates that contradict each other: a death before a birth, a child born before a biological parent or over a year after one died, a partnership starting before a birth, or someone living at over 110.',
  },
  'link-parents': { label: 'Likely parents', base: 80, when: 'Someone with one parent whose only partner isn’t linked to them, or with no parents and a full sibling whose parents are recorded.' },
  parents: { label: 'Parents', base: 62, when: 'Someone linked to others but with no parents recorded.' },
  'link-partners': { label: 'Likely couples', base: 60, when: 'Two people who are both parents of the same child (not as donor or surrogate) with no partnership recorded between them.' },
  unlinked: { label: 'Not linked', base: 55, when: 'Someone with no relationships at all.' },
  name: { label: 'Names', base: 50, when: 'Someone with no name.' },
  'birth-date': {
    label: 'Birth dates',
    base: 48,
    when: 'Someone within three links without a birth year. It estimates one from siblings, then partners, then children (using how old parents in this tree usually are), then parents.',
  },
  gender: { label: 'Gender', base: 44, when: 'Someone within three links with no gender, pronouns, or choice of relationship words, so they’re called “parent” or “sibling”.' },
  living: { label: 'Living or not', base: 42, when: 'Someone born 90 to 110 years ago with nothing recorded to say whether they’re alive.' },
  spelling: { label: 'Spellings', base: 40, when: 'Two surnames that sound alike (by Soundex) and differ by a letter or two, carried by a parent and child or by siblings.' },
  'partnership-date': { label: 'Partnership dates', base: 36, when: 'A couple within two links with no start date, when at least one of them has a birth date.' },
  'death-date': { label: 'Death dates', base: 34, when: 'Someone within three links recorded as deceased without a death year.' },
  sources: { label: 'Sources', base: 32, when: 'A tree of five or more people where nothing cites a source.' },
  photo: { label: 'Photos', base: 30, when: 'Fewer than half the people have a photo. It names the closest person without one.' },
  birthplace: { label: 'Birthplaces', base: 28, when: 'Someone within two links with a birth date but no birthplace.' },
  'cite-birth': { label: 'Citations', base: 26, when: 'The tree cites sources, but the birth of someone within two links doesn’t.' },
  occupation: { label: 'Occupations', base: 20, when: 'Someone in the home person’s direct line, born over 18 years ago, with no occupation.' },
};

/** How much a suggestion counts by how many links separate its person from the home person: 0, 1, 2, 3. */
export const CLOSENESS = [1, 0.85, 0.7, 0.55];
/** Four or more links away; linked to the home person by no chain at all; or no home person set. */
export const CLOSENESS_FAR = 0.4;
export const CLOSENESS_UNLINKED = 0.3;
export const CLOSENESS_NO_HOME = 0.6;
/** Extra weight for parents of the home person's direct line, and for asking a living elder. */
export const DIRECT_LINE = 1.4;
export const ELDER = 1.25;
/** Each further suggestion of a kind counts this much of the one before it when ordering. */
export const VARIETY = 0.7;

/** The field of the person editor a suggestion opens at, so it lands on what's being asked for. */
export type PersonField = 'name' | 'birth-date' | 'birth-place' | 'death-date' | 'occupation' | 'photo' | 'gender' | 'living';

export type SuggestionAction =
  /** Open the add-relative dialog: a new person, or someone already in the tree. */
  | { kind: 'add-relative'; personId: Id; relation: 'parent' | 'child' | 'partner' | 'sibling' | 'social'; existing?: boolean }
  /** Link people already in the tree as this person's parents. */
  | { kind: 'link-parents'; personId: Id; parentIds: Id[] }
  | { kind: 'edit'; personId: Id; field: PersonField }
  /** Answer "still living?" without opening anything. */
  | { kind: 'set-living'; personId: Id; living: boolean }
  /** Set a gender (and the pronouns it implies, if none are recorded). */
  | { kind: 'set-gender'; personId: Id; identity: 'woman' | 'man' | 'nonbinary' }
  /** Record a birth date as given (an estimate, "EST 1930"), without opening anything. */
  | { kind: 'set-birth'; personId: Id; date: string }
  /** "No": the suggestion doesn't apply, so it's dismissed. */
  | { kind: 'dismiss' }
  | { kind: 'merge'; personId: Id; otherId: Id }
  | { kind: 'set-home'; personId: Id }
  /** Add a partnership between two people already in the tree. */
  | { kind: 'link-partners'; personId: Id; otherId: Id }
  | { kind: 'edit-relationship'; relId: Id }
  | { kind: 'cite'; personId: Id; eventId?: Id }
  /** Search the People view. */
  | { kind: 'search'; query: string }
  | { kind: 'view'; view: 'sources' | 'media' };

export interface Suggestion {
  /** Stable across edits, for remembering dismissals: kind plus the people it's about. */
  id: string;
  kind: SuggestionKind;
  title: string;
  /** Why it's worth doing, or what we already know that helps. */
  detail: string;
  people: Id[];
  action: SuggestionAction;
  actionLabel: string;
  /**
   * Answers shown in place of the one action button, for questions with short answers ("Is she
   * still living?" Yes / No). The first is the main one.
   */
  choices?: { label: string; action: SuggestionAction }[];
  /** Higher is more useful; suggestions come sorted by it. */
  score: number;
}

/** Family links between the home person and everyone else (friends count too); undefined if unreachable. */
function distances(g: Graph, home: Id | undefined): Map<Id, number> {
  const dist = new Map<Id, number>();
  if (!home || !g.tree.people[home]) return dist;
  dist.set(home, 0);
  let frontier = [home];
  for (let d = 1; frontier.length; d++) {
    const next: Id[] = [];
    for (const id of frontier)
      for (const n of neighborsOf(g, id))
        if (!dist.has(n)) {
          dist.set(n, d);
          next.push(n);
        }
    frontier = next;
  }
  return dist;
}

const round5 = (y: number) => Math.round(y / 5) * 5;
const median = (xs: number[]) => {
  const s = [...xs].sort((a, b) => a - b);
  const m = Math.floor(s.length / 2);
  return s.length % 2 ? s[m] : (s[m - 1] + s[m]) / 2;
};

export function suggestions(tree: Tree, now = new Date()): Suggestion[] {
  const people = Object.values(tree.people);
  if (!people.length) return [];
  const g = graphOf(tree);
  const out: Suggestion[] = [];
  const name = (id: Id) => displayName(tree.people[id]);
  const home = tree.homePersonId && tree.people[tree.homePersonId] ? tree.homePersonId : undefined;
  const dist = distances(g, home);
  // Gaps near the home person matter most; without one, everyone counts the same.
  const near = (id: Id) => {
    if (!home) return CLOSENESS_NO_HOME;
    const d = dist.get(id);
    return d === undefined ? CLOSENESS_UNLINKED : CLOSENESS[d] ?? CLOSENESS_FAR;
  };
  // "Within n links" of the home person; with no home person, distance can't be measured, so everyone is.
  const within = (id: Id, n: number) => !home || (dist.get(id) ?? Infinity) <= n;
  const directLine = home ? new Set([home, ...ancestorIds(g, home)]) : new Set<Id>();
  const homeName = home ? shortName(tree.people[home]) : '';
  const year = (p: Person) => dateYear(birthDate(p));

  // How old parents in this tree usually are at a birth, for estimating missing birth years.
  const parentAges = Object.values(tree.relationships).flatMap((r) => {
    if (r.kind !== 'parent' || r.type !== 'biological' || !tree.people[r.a] || !tree.people[r.b]) return [];
    const span = yearsBetween(birthDate(tree.people[r.a]), birthDate(tree.people[r.b]));
    return span ? [midAge(span)] : [];
  });
  const gap = parentAges.length >= 3 ? Math.round(median(parentAges)) : 28;

  const citesAnything = people.some((p) => p.citations?.length || p.events.some((e) => e.citations?.length)) || Object.values(tree.relationships).some((r) => r.citations?.length);

  // ---- The point of view: everything else is described from the home person.
  if (!home && people.length >= 2) {
    const hub = [...people].sort((a, b) => neighborsOf(g, b.id).length - neighborsOf(g, a.id).length)[0];
    out.push({
      id: 'home',
      kind: 'home',
      title: `Make ${name(hub.id)} the home person?`,
      detail: 'Relationship words like "grandmother" are worked out from the home person, and suggestions start from them. Usually that\'s you.',
      people: [hub.id],
      action: { kind: 'set-home', personId: hub.id },
      actionLabel: 'Make home person',
      choices: [
        { label: 'Yes', action: { kind: 'set-home', personId: hub.id } },
        { label: 'No', action: { kind: 'dismiss' } },
      ],
      score: SUGGESTION_RULES.home.base,
    });
  }

  // ---- Probably the same person, entered twice.
  const byName = new Map<string, Person[]>();
  for (const p of people) {
    // A surname alone ("Hatefi") is a placeholder for someone whose first name isn't known, not a match.
    if (!named(p) || !primaryName(p)?.given.trim()) continue;
    const k = normalize(displayName(p));
    byName.set(k, [...(byName.get(k) ?? []), p]);
  }
  for (const same of byName.values()) {
    if (same.length < 2 || same.length > 30) continue;
    // One pair per name at a time: once it's merged or dismissed, the next pair (if any) shows.
    let found = false;
    for (let i = 0; i < same.length && !found; i++)
      for (let j = i + 1; j < same.length && !found; j++) {
        const [a, b] = [same[i], same[j]];
        const reason = duplicateReason(g, a, b);
        if (!reason) continue;
        found = true;
        const ids = [a.id, b.id].sort();
        out.push({
          id: `duplicate:${ids.join(':')}`,
          kind: 'duplicate',
          title: `Is ${name(a.id)} in the tree twice?`,
          detail: `Two records have this name, and ${reason}. Merging keeps everything both of them have.`,
          people: [a.id, b.id],
          action: { kind: 'merge', personId: a.id, otherId: b.id },
          actionLabel: 'Compare and merge',
          choices: [
            { label: 'Compare and merge', action: { kind: 'merge', personId: a.id, otherId: b.id } },
            { label: 'Different people', action: { kind: 'dismiss' } },
          ],
          score: SUGGESTION_RULES.duplicate.base * Math.max(near(a.id), near(b.id)),
        });
      }
  }

  for (const p of people) {
    const parents = parentsOf(g, p.id);
    const w = near(p.id);
    const isHome = p.id === home;
    const { subj, obj, poss } = pronounsOf(p);
    const they = subj === 'they';

    // ---- Someone without a name: that comes before anything else about them.
    if (!named(p)) {
      const linked = neighborsOf(g, p.id)[0];
      out.push({
        id: `name:${p.id}`,
        kind: 'name',
        title: linked ? `Who is the unnamed person linked to ${name(linked)}?` : 'Who is the unnamed person?',
        detail: 'Even a first name, or a placeholder such as "Grandmother", makes them findable and readable in the chart.',
        people: [p.id, ...(linked ? [linked] : [])],
        action: { kind: 'edit', personId: p.id, field: 'name' },
        actionLabel: 'Add a name',
        score: SUGGESTION_RULES.name.base * w,
      });
      continue;
    }

    // ---- Parents who are probably already in the tree.
    let linkable: { ids: Id[]; why: string } | undefined;
    if (parents.length === 1) {
      // A lone parent's only partner, who isn't linked to the child.
      const partners = partnersOf(g, parents[0]).filter((q) => q !== p.id);
      const q = partners.length === 1 ? partners[0] : undefined;
      const qy = q ? year(tree.people[q]) : undefined;
      const py = year(p);
      const plausible = qy === undefined || py === undefined || (py - qy >= 12 && py - qy <= 60);
      if (q && plausible && !parentsOf(g, q).includes(p.id))
        linkable = { ids: [q], why: `${name(q)} is ${name(parents[0])}'s partner but isn't linked to ${shortName(p)}` };
    } else if (!parents.length) {
      // A brother or sister whose parents are recorded.
      const sib = siblingsOf(g, p.id).find((s) => s.explicit && ['full', 'unknown'].includes(s.type) && parentsOf(g, s.id).length);
      if (sib) {
        const ids = parentsOf(g, sib.id).filter((id) => id !== p.id);
        if (ids.length) linkable = { ids, why: `${name(sib.id)} is ${shortName(p)}'s sibling, and ${ids.length === 1 ? 'that parent is' : 'those parents are'} recorded for ${pronounsOf(tree.people[sib.id]).obj}` };
      }
    }
    if (linkable) {
      const names = linkable.ids.map(name);
      out.push({
        id: `link-parents:${p.id}`,
        kind: 'link-parents',
        title: `Is ${names.join(' and ')} also ${name(p.id)}'s ${linkable.ids.length === 1 ? 'parent' : 'parents'}?`,
        detail: `${linkable.why}.`,
        people: [p.id, ...linkable.ids],
        action: { kind: 'link-parents', personId: p.id, parentIds: linkable.ids },
        actionLabel: linkable.ids.length === 1 ? 'Add as parent' : 'Add as parents',
        choices: [
          { label: 'Yes', action: { kind: 'link-parents', personId: p.id, parentIds: linkable.ids } },
          { label: 'No', action: { kind: 'dismiss' } },
        ],
        score: SUGGESTION_RULES['link-parents'].base * w,
      });
    }

    // ---- Someone who isn't connected to anyone.
    if (people.length > 1 && !neighborsOf(g, p.id).length) {
      out.push({
        id: `unlinked:${p.id}`,
        kind: 'unlinked',
        title: `How is ${name(p.id)} connected?`,
        detail: `${name(p.id)} isn't linked to anyone else in the tree yet: no family, partners, or friends.`,
        people: [p.id],
        // They're most likely related to someone already here, so start there.
        action: { kind: 'add-relative', personId: p.id, relation: 'parent', existing: true },
        actionLabel: 'Link to someone',
        score: SUGGESTION_RULES.unlinked.base,
      });
    }

    // ---- The next generation back.
    if (!parents.length && !linkable && neighborsOf(g, p.id).length + (isHome ? 1 : 0) > 0) {
      const age = isLiving(p) ? ageOf(p, now) : undefined;
      const elder = age && age.age >= 60 && age.age <= 110;
      out.push({
        id: `parents:${p.id}`,
        kind: 'parents',
        title: `Add ${name(p.id)}'s parents`,
        detail: elder
          ? `${name(p.id)} is ${ageText(age!)}. Ask ${obj} about ${poss} parents and grandparents; ${subj} may remember names and places no record has.`
          : directLine.has(p.id) && !isHome
            ? `${homeName}'s direct line stops at ${name(p.id)}. ${p.events.some((e) => e.type === 'birth' && e.place) ? `Records from where ${subj} ${they ? 'were' : 'was'} born may name ${poss} parents.` : `Adding ${poss} parents takes the tree back a generation.`}`
            : isHome
              ? `${name(p.id)} is the home person, so the rest of the tree grows from here.`
              : `No parents are recorded for ${name(p.id)} yet.`,
        people: [p.id],
        action: { kind: 'add-relative', personId: p.id, relation: 'parent' },
        actionLabel: 'Add a parent',
        choices: [
          { label: 'Add a parent', action: { kind: 'add-relative', personId: p.id, relation: 'parent' } },
          { label: 'Pick from the tree', action: { kind: 'add-relative', personId: p.id, relation: 'parent', existing: true } },
        ],
        score: SUGGESTION_RULES.parents.base * w * (directLine.has(p.id) ? DIRECT_LINE : 1) * (elder ? ELDER : 1),
      });
    }

    // ---- Dates and places, for the people closest to home.
    const dated = year(p) !== undefined;
    if (!dated && within(p.id, 3)) {
      const guess = estimateBirth(g, p, gap);
      out.push({
        id: `birth-date:${p.id}`,
        kind: 'birth-date',
        title: `When was ${name(p.id)} born?`,
        detail: guess ? `Probably around ${guess.year}, going by ${guess.from}. Even a year helps place ${obj} in time.` : `Even a year helps place ${obj} in time and makes ages and birthdays work.`,
        people: [p.id],
        action: { kind: 'edit', personId: p.id, field: 'birth-date' },
        actionLabel: 'Add birth date',
        // An estimate is better than nothing, and is saved as one ("estimated 1930"), never as a known date.
        choices: guess
          ? [
              { label: 'Add birth date', action: { kind: 'edit', personId: p.id, field: 'birth-date' } },
              { label: `Use estimated ${guess.year}`, action: { kind: 'set-birth', personId: p.id, date: `EST ${guess.year}` } },
            ]
          : undefined,
        score: SUGGESTION_RULES['birth-date'].base * w,
      });
    }

    // Born 90 to 110 years ago with nothing to say whether they're alive: ages and countdowns guess wrong.
    const by = year(p);
    if (p.living === null && lifeStatus(p) === 'unknown' && by !== undefined && now.getFullYear() - by >= 90 && !presumedDeceased(p, [], now)) {
      out.push({
        id: `living:${p.id}`,
        kind: 'living',
        title: `Is ${name(p.id)} still living?`,
        detail: `${name(p.id)} was born in ${by}. Recording whether ${subj} ${they ? 'are' : 'is'} alive keeps ages and birthday countdowns right.`,
        people: [p.id],
        action: { kind: 'edit', personId: p.id, field: 'living' },
        actionLabel: 'Record it',
        choices: [
          { label: 'Yes', action: { kind: 'set-living', personId: p.id, living: true } },
          { label: 'No', action: { kind: 'set-living', personId: p.id, living: false } },
        ],
        score: SUGGESTION_RULES.living.base * w,
      });
    }

    if (isDeceased(p) && dateYear(deathDate(p)) === undefined && within(p.id, 3)) {
      out.push({
        id: `death-date:${p.id}`,
        kind: 'death-date',
        title: `When did ${name(p.id)} die?`,
        detail: `${name(p.id)} is recorded as deceased without a date. Even a year gives ${poss} age and lifespan.`,
        people: [p.id],
        action: { kind: 'edit', personId: p.id, field: 'death-date' },
        actionLabel: 'Add death date',
        score: SUGGESTION_RULES['death-date'].base * w,
      });
    }

    if (dated && !p.events.some((e) => e.type === 'birth' && e.place?.trim()) && within(p.id, 2)) {
      out.push({
        id: `birthplace:${p.id}`,
        kind: 'birthplace',
        title: `Where was ${name(p.id)} born?`,
        detail: `A birthplace points to the records worth searching for ${obj}, and shows where the family has lived.`,
        people: [p.id],
        action: { kind: 'edit', personId: p.id, field: 'birth-place' },
        actionLabel: 'Add birthplace',
        score: SUGGESTION_RULES.birthplace.base * w,
      });
    }

    // Neutral words by default rather than by choice.
    if (p.gender.identity === 'unknown' && !p.pronouns?.trim() && (p.terms ?? 'auto') === 'auto' && within(p.id, 3)) {
      out.push({
        id: `gender:${p.id}`,
        kind: 'gender',
        title: `What are ${name(p.id)}'s gender and pronouns?`,
        detail: `With neither recorded, ${name(p.id)} is described with neutral words such as “parent” and “sibling”. If that's right, dismiss this.`,
        people: [p.id],
        action: { kind: 'edit', personId: p.id, field: 'gender' },
        actionLabel: 'Add gender',
        choices: [
          { label: 'Woman', action: { kind: 'set-gender', personId: p.id, identity: 'woman' } },
          { label: 'Man', action: { kind: 'set-gender', personId: p.id, identity: 'man' } },
          { label: 'Nonbinary', action: { kind: 'set-gender', personId: p.id, identity: 'nonbinary' } },
          { label: 'Other…', action: { kind: 'edit', personId: p.id, field: 'gender' } },
        ],
        score: SUGGESTION_RULES.gender.base * w,
      });
    }

    const birth = p.events.find((e) => e.type === 'birth');
    if (citesAnything && dated && birth && !birth.citations?.length && !p.citations?.length && within(p.id, 2)) {
      out.push({
        id: `cite-birth:${p.id}`,
        kind: 'cite-birth',
        title: `Cite a source for ${name(p.id)}'s birth`,
        detail: 'A birth certificate, civil birth register, or census entry backs up the date and place, and tells anyone reading where to look.',
        people: [p.id],
        action: { kind: 'cite', personId: p.id, eventId: birth.id },
        actionLabel: 'Add a citation',
        score: SUGGESTION_RULES['cite-birth'].base * w,
      });
    }

    const adult = by !== undefined ? now.getFullYear() - by > 18 : isDeceased(p);
    if (directLine.has(p.id) && !isHome && adult && !p.events.some((e) => e.type === 'occupation')) {
      out.push({
        id: `occupation:${p.id}`,
        kind: 'occupation',
        title: `What did ${name(p.id)} do for work?`,
        detail:
          isDeceased(p) || (by !== undefined && now.getFullYear() - by >= 90)
            ? `Census, military, and civil records often say, and a job is a clue to where else ${subj} ${they ? 'appear' : 'appears'} in the records.`
            : `What someone did says a lot about their life, and it's an easy question to ask ${obj} or those who know ${obj}.`,
        people: [p.id],
        action: { kind: 'edit', personId: p.id, field: 'occupation' },
        actionLabel: 'Add occupation',
        score: SUGGESTION_RULES.occupation.base * w,
      });
    }
  }

  // ---- Dates that contradict each other, one suggestion per person.
  const problems = new Map<Id, DateProblem[]>();
  for (const x of dateProblems(tree, now)) problems.set(x.personId, [...(problems.get(x.personId) ?? []), x]);
  for (const [id, list] of problems) {
    out.push({
      id: `dates:${id}`,
      kind: 'dates',
      title: `Check ${name(id)}'s dates`,
      detail: `${list[0].detail}${list.length > 1 ? ` ${list.length - 1} more of ${pronounsOf(tree.people[id]).poss} dates ${list.length === 2 ? 'contradicts' : 'contradict'} another too.` : ''} One of them is probably a typo.`,
      people: [...new Set([id, ...list.map((x) => x.otherId).filter((x): x is Id => !!x)])],
      action: { kind: 'edit', personId: id, field: list[0].kind === 'too-old' ? 'living' : 'birth-date' },
      actionLabel: 'Check dates',
      choices: dateChoices(tree, id, list[0]),
      score: SUGGESTION_RULES.dates.base * near(id),
    });
  }

  // ---- Two parents of the same child with no partnership between them.
  const couples = new Map<string, { a: Id; b: Id; kids: Id[] }>();
  for (const c of people) {
    const ps = (g.parentRels.get(c.id) ?? []).filter((r) => r.type !== 'donor' && r.type !== 'surrogate').map((r) => r.a);
    if (new Set(ps).size !== 2) continue;
    const [a, b] = [...new Set(ps)].sort();
    if (partnersOf(g, a).includes(b)) continue;
    const k = `${a}:${b}`;
    couples.set(k, { a, b, kids: [...(couples.get(k)?.kids ?? []), c.id] });
  }
  for (const [k, { a, b, kids }] of couples) {
    out.push({
      id: `link-partners:${k}`,
      kind: 'link-partners',
      title: `Were ${name(a)} and ${name(b)} a couple?`,
      detail: `Both are recorded as parents of ${name(kids[0])}${kids.length > 1 ? ` and ${kids.length - 1} more ${kids.length === 2 ? 'child' : 'children'}` : ''}, but no partnership is recorded between them. If they were together, even briefly, recording it draws them side by side in the chart.`,
      people: [a, b],
      action: { kind: 'link-partners', personId: a, otherId: b },
      actionLabel: 'Add partnership',
      choices: [
        { label: 'Yes', action: { kind: 'link-partners', personId: a, otherId: b } },
        { label: 'No', action: { kind: 'dismiss' } },
      ],
      score: SUGGESTION_RULES['link-partners'].base * Math.max(near(a), near(b)),
    });
  }

  // ---- Couples with no start date.
  for (const r of Object.values(tree.relationships)) {
    if (r.kind !== 'partner' || !tree.people[r.a] || !tree.people[r.b] || countableDate(r.start) || r.start?.trim()) continue;
    const w = Math.max(near(r.a), near(r.b));
    if (!(within(r.a, 2) || within(r.b, 2)) || (year(tree.people[r.a]) === undefined && year(tree.people[r.b]) === undefined)) continue;
    const wed = r.type === 'married';
    // Countdowns only counts down to anniversaries of current couples who are both alive.
    const counted = r.status === 'current' && !isDeceased(tree.people[r.a]) && !isDeceased(tree.people[r.b]);
    out.push({
      id: `partnership-date:${r.id}`,
      kind: 'partnership-date',
      title: wed ? `When did ${name(r.a)} and ${name(r.b)} marry?` : `When did ${name(r.a)} and ${name(r.b)} get together?`,
      detail: `${wed ? 'A wedding date' : 'A start date'} ${counted ? 'puts their anniversary in Countdowns and ' : ''}places ${wed ? 'the marriage' : 'the partnership'} in the family's story.${wed ? ' Marriage records are among the easiest to find.' : ''}`,
      people: [r.a, r.b],
      action: { kind: 'edit-relationship', relId: r.id },
      actionLabel: 'Add date',
      score: SUGGESTION_RULES['partnership-date'].base * w,
    });
  }

  // ---- Surnames that sound alike within one family: Wilmouth and Wilmot.
  const surname = (id: Id) => (primaryName(tree.people[id])?.surname ?? '').trim();
  const bySurname = new Map<string, Id[]>();
  for (const p of people) {
    const k = normalize(surname(p.id));
    if (k) bySurname.set(k, [...(bySurname.get(k) ?? []), p.id]);
  }
  const variants = new Map<string, { a: Id; b: Id; how: string }>();
  const consider = (a: Id, b: Id, how: string) => {
    const [x, y] = [normalize(surname(a)), normalize(surname(b))];
    // Single words only: "Rivera" and "Rivera-Okafor" share a Soundex code but are a double name, not a variant.
    if (!x || !y || x === y || /\s/.test(x) || /\s/.test(y) || x.includes(y) || y.includes(x)) return;
    if (soundex(x) !== soundex(y) || editDistance(x, y) > 2) return;
    const k = [x, y].sort().join(':');
    if (!variants.has(k)) variants.set(k, { a, b, how });
  };
  for (const r of Object.values(tree.relationships))
    if (r.kind === 'parent' && tree.people[r.a] && tree.people[r.b]) consider(r.a, r.b, `${name(r.a)} is ${name(r.b)}'s parent`);
  for (const p of people) for (const sib of siblingsOf(g, p.id)) if (tree.people[sib.id]) consider(p.id, sib.id, `${name(p.id)} and ${name(sib.id)} are siblings`);
  for (const [k, { a, b, how }] of variants) {
    const [x, y] = [surname(a), surname(b)];
    const [nx, ny] = [bySurname.get(normalize(x))!.length, bySurname.get(normalize(y))!.length];
    out.push({
      id: `spelling:${k}`,
      kind: 'spelling',
      title: `${x} or ${y}?`,
      detail: `${nx} ${nx === 1 ? 'person is' : 'people are'} recorded as ${x} and ${ny} as ${y}, and ${how}. Spellings did drift over time, but if it's one family name, one spelling (with the other kept as another name) makes them easier to find together.`,
      people: [a, b],
      action: { kind: 'search', query: `surname:~${normalize(x)}` },
      // A sound-alike search: it finds these two spellings and any others that sound the same.
      actionLabel: 'Find sound-alikes',
      score: SUGGESTION_RULES.spelling.base * Math.max(near(a), near(b)),
    });
  }

  // ---- Faces and sources, once there's a tree to put them in.
  if (people.length >= 5) {
    const pictured = new Set(Object.values(tree.media).filter((m) => m.kind === 'image').flatMap((m) => m.people.map((x) => x.personId)));
    const withPhoto = people.filter((p) => p.photo || pictured.has(p.id)).length;
    const first = [...people].filter((p) => named(p) && !p.photo && !pictured.has(p.id)).sort((a, b) => near(b.id) - near(a.id))[0];
    if (first && withPhoto < people.length / 2) {
      out.push({
        id: 'photo',
        kind: 'photo',
        title: `Add a photo of ${name(first.id)}`,
        detail: `${withPhoto ? `Only ${withPhoto} of ${people.length}` : `None of the ${people.length}`} people have a photo. Faces make the chart much quicker to read.`,
        people: [first.id],
        action: { kind: 'edit', personId: first.id, field: 'photo' },
        actionLabel: 'Add a photo',
        score: SUGGESTION_RULES.photo.base,
      });
    }
    if (!citesAnything) {
      out.push({
        id: 'sources',
        kind: 'sources',
        title: 'Note where your facts came from',
        detail: 'Nothing in the tree cites a source yet. A census, certificate, or conversation noted now saves searching for it again later.',
        people: [],
        action: { kind: 'view', view: 'sources' },
        actionLabel: 'Open Sources',
        score: SUGGESTION_RULES.sources.base,
      });
    }
  }

  return varied(out);
}

/**
 * Best first, but with less weight on each further suggestion of a kind already listed, so the top
 * of the list mixes kinds instead of being ten "add parents" in a row.
 */
function varied(list: Suggestion[]): Suggestion[] {
  const sorted = [...list].sort((a, b) => b.score - a.score || a.id.localeCompare(b.id));
  const seen = new Map<SuggestionKind, number>();
  const ranked = sorted.map((s) => {
    const n = seen.get(s.kind) ?? 0;
    seen.set(s.kind, n + 1);
    return { s, rank: s.score * Math.pow(VARIETY, n) };
  });
  return ranked.sort((a, b) => b.rank - a.rank || a.s.id.localeCompare(b.s.id)).map((x) => x.s);
}

/** Where to look for a date problem: this person's own date first, then the other person's. */
function dateChoices(tree: Tree, id: Id, x: DateProblem): { label: string; action: SuggestionAction }[] {
  const short = (pid: Id) => shortName(tree.people[pid]);
  const own: Record<DateProblem['kind'], { label: string; field: PersonField }> = {
    'death-before-birth': { label: `Check ${short(id)}'s birth`, field: 'birth-date' },
    'too-old': { label: `Still living?`, field: 'living' },
    'before-parent': { label: `Check ${short(id)}'s birth`, field: 'birth-date' },
    'after-parent-died': { label: `Check ${short(id)}'s birth`, field: 'birth-date' },
    'partner-before-birth': { label: `Check ${short(id)}'s birth`, field: 'birth-date' },
  };
  const choices: { label: string; action: SuggestionAction }[] = [{ label: own[x.kind].label, action: { kind: 'edit', personId: id, field: own[x.kind].field } }];
  if (x.kind === 'death-before-birth') choices.push({ label: `Check the death`, action: { kind: 'edit', personId: id, field: 'death-date' } });
  if (x.otherId && x.kind === 'before-parent') choices.push({ label: `Check ${short(x.otherId)}'s birth`, action: { kind: 'edit', personId: x.otherId, field: 'birth-date' } });
  if (x.otherId && x.kind === 'after-parent-died') choices.push({ label: `Check ${short(x.otherId)}'s death`, action: { kind: 'edit', personId: x.otherId, field: 'death-date' } });
  return choices;
}

/** Letters to change to turn one word into the other. */
function editDistance(a: string, b: string): number {
  let prev = Array.from({ length: b.length + 1 }, (_, j) => j);
  for (let i = 1; i <= a.length; i++) {
    const row = [i];
    for (let j = 1; j <= b.length; j++) row[j] = Math.min(prev[j] + 1, row[j - 1] + 1, prev[j - 1] + (a[i - 1] === b[j - 1] ? 0 : 1));
    prev = row;
  }
  return prev[b.length];
}

/** Has a name to show (not "Unnamed person"). */
const named = (p: Person) => !!nameToString(primaryName(p));

/** Pronouns for generated sentences: as recorded, else as their relationship words suggest (mother → she). */
function pronounsOf(p: Person): { subj: string; obj: string; poss: string } {
  if (p.pronouns?.trim()) return pronounSet(p);
  const t = termGender(p);
  return t === 'feminine' ? { subj: 'she', obj: 'her', poss: 'her' } : t === 'masculine' ? { subj: 'he', obj: 'him', poss: 'his' } : { subj: 'they', obj: 'them', poss: 'their' };
}

function ancestorIds(g: Graph, id: Id): Id[] {
  const seen = new Set<Id>();
  const stack = [id];
  while (stack.length)
    for (const p of parentsOf(g, stack.pop()!))
      if (!seen.has(p)) {
        seen.add(p);
        stack.push(p);
      }
  seen.delete(id);
  return [...seen];
}

/** Why two people with the same name are probably one person, or undefined if they probably aren't. */
function duplicateReason(g: Graph, a: Person, b: Person): string | undefined {
  // Linked to each other: a parent and the child named after them, or a couple sharing a name.
  if (neighborsOf(g, a.id).includes(b.id)) return undefined;
  const ga = a.gender.identity;
  const gb = b.gender.identity;
  if (ga !== 'unknown' && gb !== 'unknown' && ga !== gb) return undefined;
  const ya = dateYear(birthDate(a));
  const yb = dateYear(birthDate(b));
  // A child's two parents are a couple, even when both are only known by the family name.
  for (const c of childrenOf(g, a.id)) {
    const ps = parentsOf(g, c);
    if (ps.includes(b.id) && ps.length <= 2) return undefined;
  }
  // Different parents: cousins named after the same grandparent, not one person.
  const parentsA = parentsOf(g, a.id);
  const parentsB = parentsOf(g, b.id);
  if (parentsA.length && parentsB.length && !parentsA.some((p) => parentsB.includes(p))) return undefined;
  if (ya !== undefined && yb !== undefined) {
    if (ya !== yb) return undefined;
    const da = countableDate(birthDate(a));
    const db = countableDate(birthDate(b));
    if (da?.month && db?.month && da.month !== db.month) return undefined;
    if (da?.day && db?.day && da.day !== db.day) return undefined;
    return `both were born in ${ya}`;
  }
  const pa = normalize(a.events.find((e) => e.type === 'birth')?.place ?? '');
  if (pa && pa === normalize(b.events.find((e) => e.type === 'birth')?.place ?? '')) return 'both were born in the same place';
  // The same relative in the same role. A grandfather and the grandson named after him share a
  // relative too (the son between them), but as his parent and his child, so that doesn't count.
  const who = (id: Id) => displayName(g.tree.people[id]);
  const sameParent = parentsA.find((p) => parentsB.includes(p));
  if (sameParent) return `both are recorded as children of ${who(sameParent)}`;
  const sameChild = childrenOf(g, a.id).find((c) => parentsOf(g, c).includes(b.id));
  if (sameChild) return `both are recorded as parents of ${who(sameChild)}`;
  const samePartner = partnersOf(g, a.id).find((q) => partnersOf(g, b.id).includes(q));
  if (samePartner) return `both are recorded as partners of ${who(samePartner)}`;
  return undefined;
}

/** A rough birth year from close family's: siblings, then partners, then children, then parents. */
function estimateBirth(g: Graph, p: Person, gap: number): { year: number; from: string } | undefined {
  const years = (ids: Id[]) => ids.map((id) => dateYear(birthDate(g.tree.people[id]))).filter((y): y is number => y !== undefined);
  const sib = years(siblingsOf(g, p.id).map((s) => s.id));
  if (sib.length) return { year: round5(median(sib)), from: sib.length === 1 ? "a sibling's birth year" : "siblings' birth years" };
  const partner = years(partnersOf(g, p.id));
  if (partner.length) return { year: round5(median(partner)), from: "a partner's birth year" };
  const kids = years((g.childRels.get(p.id) ?? []).filter((r) => r.type === 'biological').map((r) => r.b));
  if (kids.length) return { year: round5(Math.min(...kids) - gap), from: kids.length === 1 ? "a child's birth year" : "the eldest child's birth year" };
  const parents = years((g.parentRels.get(p.id) ?? []).filter((r) => r.type === 'biological').map((r) => r.a));
  if (parents.length) return { year: round5(Math.max(...parents) + gap), from: parents.length === 1 ? "a parent's birth year" : "parents' birth years" };
  return undefined;
}
